/**
 * DebounceStack - A stack data structure with debounced callbacks and transition detection
 *
 * This class provides a stack implementation that queues operations during a debounce window
 * and intelligently processes them to distinguish between:
 * - Stacking: Adding a new item on top of existing items (e.g., opening size-guide on top of quickshop)
 * - Transitions: Replacing one item with another (e.g., closing quickshop and opening post-add-to-cart)
 * - Closing: Removing an item from the stack
 *
 * ## How It Works
 *
 * Operations (push/pop) are queued rather than immediately applied. After a debounce interval
 * (default 100ms), all queued operations are processed with transition detection:
 *
 * 1. **Transition Detection**: If a `pop(X)` is immediately followed by a `push(Y)`, it's treated
 *    as a transition where X is replaced with Y, rather than stacking Y on top of X.
 *
 * 2. **Stacking**: A standalone `push(item)` adds the item to the top of the stack.
 *
 * 3. **Closing**: A standalone `pop(item)` removes the item from the stack.
 *
 * ## Usage Example
 *
 * ```typescript
 * const stack = new DebounceStack<string>(100);
 *
 * stack.addListener((currentStack) => {
 *   console.log('Stack state:', currentStack);
 * });
 *
 * // Stacking: quickshop → size-guide
 * stack.push('quickshop');        // After 100ms: ['quickshop']
 * stack.push('size-guide');       // After 100ms: ['quickshop', 'size-guide']
 *
 * // Transition: quickshop → post-add-to-cart
 * stack.pop('quickshop');
 * stack.push('post-add-to-cart'); // After 100ms: ['post-add-to-cart'] (replaced, not stacked)
 *
 * or, in rapid succession:
 * stack.push('post-add-to-cart');
 * stack.pop('quickshop'); // After 100ms: ['post-add-to-cart'] (removed, not stacked)
 *
 * // Closing: remove size-guide
 * stack.pop('size-guide');        // After 100ms: ['quickshop']
 * ```
 *
 * ## Use Case
 *
 * Designed for tracking dialog/modal states where:
 * - Dialogs can stack on top of each other (e.g., quickshop with size-guide overlay)
 * - Dialogs can transition from one to another (e.g., quickshop → post-add-to-cart overlay)
 * - Operations happen quickly and need to be batched to determine the actual visual state
 *
 * The debounce ensures that rapid operations (like closing one dialog and opening another)
 * are processed together, allowing the transition detection to correctly identify replacements
 * rather than incorrectly showing both dialogs in the stack.
 */
export class DebounceStack {
    items = [];
    callbacks = [];
    debounceTimer = null;
    debounceInterval;
    pendingOperations = [];
    constructor(debounceInterval = 100) {
        this.debounceInterval = debounceInterval;
    }
    /**
     * Adds an item to the top of the stack
     * @param item - The item to add
     */
    push(item) {
        console.debug('dialogStack pushing item', item);
        this.pendingOperations.push({ type: 'push', item });
        this.triggerCallbacks();
    }
    /**
     * Removes and returns the item from the top of the stack
     * @param itemToRemove - The item to remove
     */
    pop(itemToRemove) {
        console.debug('dialogStack popping itemToRemove', itemToRemove);
        this.pendingOperations.push({ type: 'pop', item: itemToRemove });
        this.triggerCallbacks();
    }
    /**
     * Returns the item at the top of the stack without removing it
     * @returns The top item, or undefined if the stack is empty
     */
    peek() {
        return this.items[this.items.length - 1];
    }
    /**
     * Returns the current size of the stack
     * @returns The number of items in the stack
     */
    size() {
        return this.items.length;
    }
    /**
     * Checks if the stack is empty
     * @returns True if the stack is empty, false otherwise
     */
    isEmpty() {
        return this.items.length === 0;
    }
    /**
     * Returns a copy of all items in the stack
     * @returns An array containing all stack items
     */
    toArray() {
        return [...this.items];
    }
    /**
     * Adds a callback function to be executed when the stack changes
     * @param callback - Function to call with the current stack state
     */
    addListener(callback) {
        this.callbacks.push(callback);
    }
    /**
     * Removes a callback function from the listeners
     * @param callback - The callback function to remove
     */
    removeListener(callback) {
        const index = this.callbacks.indexOf(callback);
        if (index > -1) {
            this.callbacks.splice(index, 1);
        }
    }
    /**
     * Clears all items from the stack
     */
    clear() {
        if (this.items.length > 0) {
            this.items = [];
            this.pendingOperations = [];
            this.triggerCallbacks();
        }
    }
    /**
     * Processes pending operations and applies transition logic:
     * - If pop X followed by push Y: treat as transition (replace X with Y)
     * - If push without preceding pop: treat as stacking (add to top)
     * - If pop without following push: treat as closing (remove from stack)
     */
    processOperations() {
        if (this.pendingOperations.length === 0) {
            return;
        }
        const operations = [...this.pendingOperations];
        this.pendingOperations = [];
        let i = 0;
        while (i < operations.length) {
            const currentOp = operations[i];
            const nextOp = operations[i + 1];
            // Detect transition: pop X followed by push Y
            if (currentOp.type === 'pop' && nextOp?.type === 'push') {
                // Replace the popped item with the pushed item
                const indexToReplace = this.items.findIndex(item => item === currentOp.item);
                if (indexToReplace > -1) {
                    // Replace at the same position (maintains stack order)
                    this.items[indexToReplace] = nextOp.item;
                }
                else {
                    // If item not found, just push the new one (fallback)
                    this.items.push(nextOp.item);
                }
                // Skip both operations
                i += 2;
            }
            else if (currentOp.type === 'push') {
                // Normal stacking: add to top
                this.items.push(currentOp.item);
                i += 1;
            }
            else if (currentOp.type === 'pop') {
                // Normal closing: remove from stack
                const indexToRemove = this.items.findIndex(item => item === currentOp.item);
                if (indexToRemove > -1) {
                    this.items.splice(indexToRemove, 1);
                }
                i += 1;
            }
            else {
                i += 1;
            }
        }
    }
    /**
     * Triggers all registered callbacks after the debounce interval
     */
    triggerCallbacks() {
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
        }
        this.debounceTimer = setTimeout(() => {
            this.processOperations();
            const stackSnapshot = this.toArray();
            this.callbacks.forEach(callback => {
                try {
                    callback(stackSnapshot);
                }
                catch (error) {
                    console.error('Error executing stack listener callback:', error);
                }
            });
            this.debounceTimer = null;
        }, this.debounceInterval);
    }
}
